import {useMutation, useQuery} from "@tanstack/react-query";
import {message} from "antd";
import {AxiosError} from "axios";
import {closePaymentModal, useFlutterwave} from "flutterwave-react-v3";
import {TProfileApiResponse} from "../redux/profile/i-profile-1";
import {TServiceApiResponse} from "../redux/service/i-service";
import {AnalyticsEvent} from "../utils/analytics";
import {useClientV2} from "../utils/hooks";
import {PublicProfileApiResponseEvent} from "./live-events/live-classes";
import {TGetProductApiResponse} from "./products";

export type TPublicProfileApiResponse = TProfileApiResponse & {
  services: TServiceApiResponse[];
  events?: PublicProfileApiResponseEvent[];
  products: TGetProductApiResponse["product"][];
  coachTimezone: {
    id: number;
    userId: number;
    coachli_timezone: string;
    createdAt: Date;
    updatedAt: Date;
  };
};
/**
 * The v1 `booking/service/:username/:slug/:id` payload.
 *
 * v2 split this bootstrap into separate endpoints, so nothing fetches this
 * shape any more. It survives purely as the source of the prop types the
 * reschedule calendar (`components/calendar/custom-calendar`) still declares
 * against; delete it once that component is migrated too.
 */
export type TPublicProfileBookingApiResponse = {
  service: TServiceApiResponse;
  profile: TProfileApiResponse;
  mentor: {
    avatar: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    displayName?: string;
  };
  days: {
    sun: {from: string; to: string}[];
    mon: {from: string; to: string}[];
    tue: {from: string; to: string}[];
    wed: {from: string; to: string}[];
    thu: {from: string; to: string}[];
    fri: {from: string; to: string}[];
    sat: {from: string; to: string}[];
  };

  timeZone: string | null;
  unavailableDates: {
    from: string;
    to: string;
  }[];
};
export type TTimeslotsPayload = {
  mentorId?: number;
  serviceId?: number;
  date: number;
  year: number;
  month: number;
  day: string;
  mentorTimeZone: string;
  userTimeZone: string;
};

/**
 * A coach's public profile, as returned by `GET public/profile?username=`.
 *
 * Typed down to what callers read today. The field that matters is `userId`:
 * the availability endpoints key off `mentorId`, and neither a bundle row nor
 * the service payload carries the coach's numeric id.
 */
export type TPublicCoachProfileApiResponse = {
  userId: number;
  username: string;
  displayName: string | null;
  user?: {
    firstName: string;
    lastName: string;
    avatar: string | null;
    coverImage: string | null;
  };
};

/**
 * What a caller supplies to book one session out of a bundle.
 *
 * Deliberately smaller than the v1 payload: v2 derives the coach, the coach's
 * timezone, the currency, the amount and the audio/video call type from the
 * service row, so sending them is neither necessary nor honoured. `emailAddress`
 * is absent by design - the route reads the customer's identity off the bearer
 * token and refuses a body field that could name someone else's bundle.
 */
export type TBundleSessionBookingPayload = {
  /** Names the exact bundle to draw the session from; goes in the path. */
  bundleReference: string;
  serviceId: string | number;
  firstName: string;
  lastName: string;
  bookingYear: string | number;
  bookingMonth: string | number;
  bookingDate: string | number;
  timeSlot: string;
  userTimezone: string;
  phoneNumber?: string | null;
  notes?: string;
  bookingQuestions?: {question: string; answer: string}[];
};
export type ServiceBookingApiResponse = {
  booking: {
    amount: number;
    bookingQuestions: {question: string; answer: string}[];
    booking_date: number;
    booking_month: number;
    booking_type: "audio" | "video";
    booking_year: number;
    coach_from_time: string;
    coach_to_time: string;
    currency: string;
    email_address: string;
    endTime: string;
    first_name: string;
    is_bundle: boolean;
    is_paid: boolean;
    last_name: string;
    mentor_timezone: string;
    notes: string;
    payment_provider: "flutterwave" | "paystack" | "stripe";
    phone_number: string | null;
    reference: string;
    serviceId: number;
    statusId: number;
    timeslot: string;
    userId: number;
    user_timezone: string;
    video_platform: "videoSdk" | "dailyCo";

    // bundle_reference: "47546447b1579e96073e4c8a81f95a2a10c37ae917d3e1ad7cb6f0afbff6a479";
    // bundle_session: 2;
    // id: 401;
    // roomId: "vzk2-wx9o-3fls";
  };
};
export type ServiceBundleBookingApiResponse = {
  bundle: {
    amount: number;
    bookingQuestions: {question: string; answer: string}[];
    booking_date: number;
    booking_month: number;
    booking_type: "audio" | "video";
    booking_year: number;
    bundle_reference: string;
    bundle_session: number;
    coach_from_time: string;
    coach_to_time: string;
    currency: string;
    email_address: string;
    endTime: string;
    first_name: string;
    id: number;
    is_bundle: boolean;
    is_paid: boolean;
    last_name: string;
    mentor_timezone: string;
    notes: string;
    payment_provider: "flutterwave" | "paystack" | "stripe";
    phone_number: string | null;
    reference: string;
    roomId: string;
    serviceId: number;
    statusId: number;
    timeslot: string;
    userId: number;
    user_timezone: string;
    video_platform: "videoSdk" | "dailyCo";
  };
};
export type ServiceBookingApiResponseState = {
  amount: number;
  bookingQuestions: {question: string; answer: string}[];
  booking_date: number;
  booking_month: number;
  booking_type: "audio" | "video";
  booking_year: number;
  bundle_reference?: string;
  bundle_session?: number;
  coach_from_time: string;
  coach_to_time: string;
  currency: string;
  email_address: string;
  endTime: string;
  first_name: string;
  id?: number;
  is_bundle: boolean;
  is_paid: boolean;
  last_name: string;
  mentor_timezone: string;
  notes: string;
  payment_provider: "flutterwave" | "paystack" | "stripe";
  phone_number: string | null;
  reference: string;
  roomId?: string;
  serviceId: number;
  statusId: number;
  timeslot: string;
  userId: number;
  user_timezone: string;
  video_platform: "videoSdk" | "dailyCo";
};

type TFlutterwaveConfig = {
  public_key: string;
  tx_ref: string;
  amount: number;
  currency?: "NGN" | string;
  customer: {
    email: string;
    phone_number: string;
    name: string;
  };
  customizations: {
    title: string;
    description: string;
    logo: string;
  };
  meta?: Record<string, unknown>;
  redirect_url?: string;
  payment_plan?: string;
  payment_options: "card, ussd, mobilemoney" | string;
  subaccounts?: Array<unknown>;
};
/** A coach's profile changes rarely; hold it across modal opens. */
const PUBLIC_PROFILE_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Resolves a coach by username, for the sake of their numeric user id.
 *
 * The booking availability endpoints (`public/booking/days`,
 * `unavailable-dates`, `time-slots`) all take `mentorId`, and the payloads a
 * customer holds identify the coach by username only, so screens that need to
 * draw a calendar fan out through here first.
 */
const useGetPublicCoachProfile = (username?: string | null) => {
  const client = useClientV2();

  return useQuery<
    TPublicCoachProfileApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["public_coach_profile", username],
    queryFn: () => {
      // Query param, not a path segment - v2 resolves by username.
      const params = new URLSearchParams({username: username ?? ""});
      return client(`public/profile?${params.toString()}`).then(
        (response) => response?.data ?? response
      );
    },
    enabled: !!username,
    staleTime: PUBLIC_PROFILE_STALE_TIME_MS,
  });
};

/** Zero-pads to two digits, matching how the booking month is stored. */
const padToTwoDigits = (value: string | number) => String(value).padStart(2, "0");

/**
 * Turns a failed bundle-session booking into something worth showing a customer.
 *
 * The api client rejects with the API's error body (`{status, message}`) rather
 * than the AxiosError, and this route answers with messages written for the
 * customer: 404 when the bundle does not exist or is not theirs, 409 when it is
 * not active, has no sessions left, or the booking interval has not elapsed. So
 * the message is passed through verbatim, and the generic line is reached only
 * when the request never got an answer at all (offline, timeout).
 */
const bundleBookingErrorMessage = (error: unknown): string => {
  const apiMessage = (error as {message?: unknown} | null | undefined)?.message;
  return typeof apiMessage === "string" && apiMessage.trim().length > 0
    ? apiMessage
    : "We couldn't schedule this session. Please try again.";
};

/**
 * Books one session out of a bundle the customer already owns.
 *
 * Posts to the customer-authenticated route, which draws the session from the
 * bundle named in the path. The public bundle checkout it replaces re-derived
 * the bundle from (service, coach, email) newest-first, so a customer holding
 * two bundles for one service could only ever draw from the newest, and an
 * exhausted bundle quietly became a brand new (unpaid) one.
 */
const useCreateBundleBooking = () => {
  const client = useClientV2();
  return useMutation({
    mutationFn: ({
      bundleReference,
      bookingQuestions,
      ...booking
    }: TBundleSessionBookingPayload) =>
      client(`customer/bookings/bundle/${encodeURIComponent(bundleReference)}/session`, {
        method: "POST",
        data: {
          firstName: booking.firstName,
          lastName: booking.lastName,
          // No emailAddress: the route takes the customer's identity from the
          // bearer token and does not accept one in the body.
          serviceId: Number(booking.serviceId),
          // The three date parts are validated as strings on v2, so numbers
          // have to be coerced or the request is rejected outright.
          bookingYear: String(booking.bookingYear),
          bookingMonth: padToTwoDigits(booking.bookingMonth),
          // Day of the month is stored unpadded, so it is not padded here.
          bookingDate: String(booking.bookingDate),
          timeSlot: booking.timeSlot,
          userTimezone: booking.userTimezone,
          ...(booking.phoneNumber ? {phoneNumber: booking.phoneNumber} : {}),
          ...(booking.notes ? {notes: booking.notes} : {}),
          // Sent as a JSON string on v2, not as an array.
          ...(bookingQuestions?.length
            ? {bookingQuestions: JSON.stringify(bookingQuestions)}
            : {}),
        },
      }),

    onError: (error: AxiosError<{message: string}>) => {
      message.error(bundleBookingErrorMessage(error));
      AnalyticsEvent("Booking", "Bundle Booking create fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Booking", "Bundle Booking create success");
    },
  });
};

const useHandleFlutterwavePayment = (
  config: TFlutterwaveConfig,
  updateStep: (step: number) => void
) => {
  const handleFlutterPayment = useFlutterwave(config);
  handleFlutterPayment({
    callback: (response) => {
      // console.log({response});
      closePaymentModal(); // this will close the modal programmatically
      if (response.status === "successful" || response.status === "completed") {
        AnalyticsEvent("Flutterwave payment", "Flutterwave payment success");
        updateStep(3);
      }
      AnalyticsEvent("Flutterwave payment", "Flutterwave payment fail");
    },
    onClose: () => {
      AnalyticsEvent("Flutterwave payment", "Closed payment modal");
    },
  });
};

export {
  useGetPublicCoachProfile,
  useCreateBundleBooking,
  bundleBookingErrorMessage,
  useHandleFlutterwavePayment,
};
